/**
 * The Woodsmen — domain model.
 *
 * Two rules govern this file.
 *
 * 1. The only scoring fact we persist is what a player made on a hole. Net
 *    scores, pops, format points, match status and every leaderboard number are
 *    derived on read, so fixing a wrong handicap or stroke index retroactively
 *    repairs the whole tournament instead of leaving stale numbers behind.
 *
 * 2. A Round is WHERE and WHEN. A Match is WHAT'S AT STAKE. They are separate
 *    because the tournament plays the same holes more than once in a day —
 *    Monday is Gozzer front, back, then front again — so a score keyed on
 *    (course, hole) would collide with itself. Scores key on (round, hole).
 */

export type PlayerId = string
export type CourseId = string
export type TeeSetId = string
export type RoundId = string
export type MatchId = string
export type GroupId = string
export type UnitId = string

// ---------------------------------------------------------------------------
// Courses
// ---------------------------------------------------------------------------

export interface Hole {
  /** Position on the physical card, 1-18. */
  number: number
  par: number
  /**
   * Stroke index, 1 = hardest, unique across the course. This is the single
   * most consequential field in the app — it decides every pop. Never guess it.
   */
  strokeIndex: number
  yards: Record<TeeSetId, number>
}

/** Some clubs publish separate ratings for each nine. Use them when they exist. */
export interface NineRatings {
  rating: number
  slope: number
  par: number
}

export interface TeeSet {
  id: TeeSetId
  name: string
  /** Hex color for the UI chip. */
  color: string
  rating: number
  slope: number
  par: number
  front?: NineRatings
  back?: NineRatings
  totalYards: number
}

export interface Course {
  id: CourseId
  name: string
  par: number
  tees: TeeSet[]
  holes: Hole[]
  /**
   * False until an admin confirms par, stroke index and yardage against the
   * physical card. Unverified courses show a banner wherever they are used,
   * because seeded course data is best-effort and stroke index decides pops.
   */
  verified: boolean
  notes?: string
}

/** Which holes a round plays. */
export type NineSelector = 'front' | 'back' | 'full18' | { holes: number[] }

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export interface Player {
  id: PlayerId
  /** Short form for tight leaderboard rows, e.g. "Scott P." */
  displayName: string
  fullName: string
  /** The sheet spells names inconsistently across pages; these let us match. */
  aliases: string[]
  handicapIndex: number
  /*
   * The Low Handicap Index: the lowest index of the last twelve months.
   *
   * GHIN's own number, and the one that settles the argument. A man arrives at
   * Bandon off an 8 that was a 6 in May, and everybody has an opinion about
   * which is the real him — the Low HI is the answer WHS itself gives, and it
   * is what the soft and hard caps are measured from.
   *
   * Display only. Nothing in the scoring engine reads it, so a missing one
   * costs nothing: it is optional precisely because it has to be looked up per
   * player and half the field will never have been asked for it.
   */
  lowIndex?: number | null
  /*
   * When his buy-in landed, or absent while Scott is still waiting on him.
   *
   * A timestamp rather than a boolean because the question that follows
   * "has he paid?" is always "when?" — and because un-marking a man is a
   * real act (it says the money everybody saw marked in was not there),
   * which is why the toggle in Setup makes you press twice to do it.
   * Display and bookkeeping only; nothing in the scoring engine reads it.
   */
  buyInPaidAt?: string | null
  /*
   * Which of the two he actually plays off. Defaults to his current index.
   *
   * Set per man rather than for the whole week, because the honest answer
   * differs by man: somebody three shots above his low is a different argument
   * from somebody sitting on it, and one toggle for twelve people would force
   * the same verdict on both.
   *
   * Only ever a pointer at one of the two numbers above — never a third number
   * of its own. A copied value would silently stop tracking the man it came
   * from the moment his index moved.
   */
  playsOff?: 'current' | 'low'
  /** Tee set per course. Falls back to the course default. */
  teeByCourse: Record<CourseId, TeeSetId>
  isAdmin: boolean
  active: boolean
  /**
   * On the roster to watch and bet, never to play.
   *
   * `active: false` means "off the roster" — a man who dropped out, whose
   * name must not come back in the picker. A spectator is the opposite kind
   * of absence: he signs in, backs runners, follows the board and shows up
   * in the book, but is never drawn into a foursome, never carries a card,
   * never appears in the standings or a quota, and never holds a flight.
   * Everything that decides golf keeps filtering on `active`; only the
   * sign-in list and the book know about this.
   */
  spectator?: boolean
}

/**
 * Handicap state frozen at the start of a round.
 *
 * Read this, never a player's current index, when scoring. If the admin adjusts
 * an index on Tuesday, Monday's finished matches must not silently re-decide
 * themselves — that has to be an explicit "apply retroactively" action.
 */
export interface RoundHandicap {
  roundId: RoundId
  playerId: PlayerId
  teeSetId: TeeSetId
  handicapIndex: number
  /** Signed — plus handicaps give strokes back. */
  courseHandicap: number
  source: 'derived' | 'manual'
  /** The sheet says quotas are "as indicated on your scorecard", so allow an override. */
  quotaTarget: number | null
}

// ---------------------------------------------------------------------------
// Formats
// ---------------------------------------------------------------------------

export type FormatKind =
  | 'individualMatchPlay'
  | 'teamHighLow'
  | 'teamCombinedNet'
  | 'teamFourBall'
  | 'quota'
  | 'strokeNet'

/**
 * How strokes are allocated within a match.
 * 'relativeToLowInUnit'  — off the low handicap in that head-to-head contest.
 * 'relativeToLowInGroup' — off the low handicap in the foursome.
 * 'full'                 — everybody plays their whole course handicap.
 */
export type NetBasis = 'relativeToLowInUnit' | 'relativeToLowInGroup' | 'full'

export interface PlacePayout {
  /** Inclusive 1-based placement range, e.g. { from: 4, to: 6 }. */
  from: number
  to: number
  points: number
  cash: number
}

export interface HeadToHeadPayout {
  win: { points: number; cash: number }
  halve: { points: number; cash: number }
  loss: { points: number; cash: number }
}

export interface IndividualMatchPlayConfig {
  kind: 'individualMatchPlay'
  netBasis: NetBasis
  payout: HeadToHeadPayout
  /** A match 5 up with 3 to play is over — "5 & 3" — and freezes. */
  closeout: boolean
}

export interface TeamHighLowConfig {
  kind: 'teamHighLow'
  netBasis: NetBasis
  /**
   * Points accrue PER HOLE. The sheet says "After each hole, both players on a
   * team record their individual NET scores" — so a 9-hole match is worth up to
   * 18 points, not 2.
   */
  pointsPerHole: { low: number; high: number }
  tieValue: number
  payout: HeadToHeadPayout
}

export interface TeamCombinedNetConfig {
  kind: 'teamCombinedNet'
  netBasis: NetBasis
  payout: HeadToHeadPayout
  closeout: boolean
}

/**
 * Four-ball. Identical to combined net everywhere except the one line that
 * matters: the side posts its LOWER net, not the sum of both.
 *
 * That single change is why it needs its own kind rather than a flag on
 * TeamCombinedNetConfig — a config carrying "add them / take the lower" reads as
 * a knob somebody might turn mid-week, and these are two different matches on
 * the sheet with two different purses.
 */
export interface TeamFourBallConfig {
  kind: 'teamFourBall'
  netBasis: NetBasis
  payout: HeadToHeadPayout
  closeout: boolean
}

export interface QuotaTable {
  eagleOrBetter: number
  birdie: number
  par: number
  bogey: number
  worse: number
}

export const DEFAULT_QUOTA_TABLE: QuotaTable = {
  eagleOrBetter: 8,
  birdie: 4,
  par: 2,
  bogey: 1,
  worse: 0,
}

export interface QuotaConfig {
  kind: 'quota'
  /** The only difference between Match 4 and Match 6. */
  rankingScope: 'group' | 'field'
  table: QuotaTable
  /** target = pointsPerPar x holes - courseHandicap(for those holes) */
  quotaFormula: { pointsPerPar: number; subtractHandicap: boolean }
  /**
   * Quota points come off GROSS versus par, never net. The handicap is already
   * absorbed by the starting target — scoring off net would count it twice and
   * make every number in Matches 4 and 6 wrong.
   */
  scoreBasis: 'gross'
  payouts: PlacePayout[]
}

export interface Flight {
  name: string
  /** Inclusive 1-based seeding range, e.g. 1-4. */
  fromSeed: number
  toSeed: number
  payouts: PlacePayout[]
}

export interface StrokeNetConfig {
  kind: 'strokeNet'
  netBasis: 'full'
  /**
   * What a hole NOBODY HOLED OUT posts, as strokes over par. Null = post nothing.
   *
   * It was a net-maximum cap, which is what the name still says. That cap was
   * removed when the championship stopped drawing pop holes — the allowance is
   * spent once as a starting figure, so there is no per-hole stroke left to raise
   * a ceiling with, and the group plays a blow-up hole at full freight.
   *
   * The key is deliberately not renamed: it is stored inside live tournament
   * documents, and a rename is a data migration for no behavioural gain. See
   * `tabulateStrokeNet`.
   */
  maxOverParNet: number | null
  /**
   * The most any ONE hole can cost, as strokes over par. Null for no maximum.
   *
   * A different rule from `maxOverParNet` above, and the two are easy to
   * confuse: that one is what a hole NOBODY HOLED OUT posts, this one is a
   * ceiling on a hole a man finished. Shawn's 2026 sheet plays a triple-bogey
   * max, so a nine on a par four counts as a seven.
   *
   * On the GROSS, deliberately. Scott's ruling: the cap is a ceiling on the
   * score, the championship still draws no pop holes, and the handicap is still
   * spent once as a starting figure — so capping the net would be spending the
   * allowance twice.
   */
  maxOverParGross: number | null
  flighted: boolean
  flights: Flight[]
}

export type FormatConfig =
  | IndividualMatchPlayConfig
  | TeamHighLowConfig
  | TeamCombinedNetConfig
  | TeamFourBallConfig
  | QuotaConfig
  | StrokeNetConfig

// ---------------------------------------------------------------------------
// Structure
// ---------------------------------------------------------------------------

export interface Round {
  id: RoundId
  /** 1..N. The canonical "round number" the tiebreak rules refer to. */
  seq: number
  label: string
  courseId: CourseId
  nine: NineSelector
  date: string
  firstTeeTime: string
  teeIntervalMinutes: number
}

export interface Match {
  id: MatchId
  roundId: RoundId
  /** Display order. Match 6 becomes three rows: 6a, 6b, 6c. */
  seq: number
  label: string
  config: FormatConfig
  status: 'scheduled' | 'live' | 'final'
  /**
   * The number the sheet and the group use — "1", or "6a". A string, because
   * Match 6 is three nines and they are 6a, 6b and 6c.
   *
   * This is the only part of a match's NAME that is stored, because it is the
   * only part that is not already a fact about the round: the course, the nine
   * and the format are all read off it. See `matchName`. Absent on the
   * championship, which has never had a number, and on a round somebody added
   * on a tee.
   */
  number?: string
  /**
   * When an admin called the round on darkness, ISO. Absent means never.
   *
   * The horn rule: every contest in the match ends as it stands — the side
   * that is up has won, square is halved, and a quota target is prorated to
   * the holes actually played. Set and cleared in Setup; clearing it brings
   * the match back to life untouched, because calling changes no score.
   * Never offered on the championship — called stroke play across flights is
   * a committee argument, not a flag.
   */
  calledAt?: string
  /**
   * Whether this match is part of the tournament, or nine holes for the fun of
   * it. Absent means yes — every match written before ad-hoc rounds existed.
   *
   * False makes the round invisible to everything that decides money or
   * position: it scores no points, settles no cash, is never chased for a
   * signature, and — the one that would have been silent — cannot hold the
   * championship seeding open by never being signed. It is still fully scored,
   * still a card, and still carries side matches, because that is the entire
   * reason somebody added it.
   *
   * Read through `matchCounts` rather than by hand, so "absent means yes" is
   * stated in exactly one place.
   */
  counts?: boolean
}

/** Whether a match feeds the money and the standings. Absent means it does. */
export const matchCounts = (m: Pick<Match, 'counts'>): boolean => m.counts !== false

export interface Group {
  id: GroupId
  roundId: RoundId
  /** 0-based. Tee time = firstTeeTime + index x interval. */
  index: number
  teeTime: string
  playerIds: PlayerId[]
  /** The optimizer may not move a pinned group. */
  pinned: boolean
  /**
   * Which flight this group carries, 1-based — championship rounds only.
   *
   * A championship foursome IS a flight, but its OCCUPANTS are unknown until
   * the standings settle, so the group needs an identity that is not its
   * players. It cannot be the index either: the index is the tee-time slot,
   * and reordering the slots is precisely the control worth having — sending
   * Flight 1 out last is a choice, and it only means anything if the flight
   * rides along when the group moves. Absent on every ordinary round.
   */
  flight?: number
}

export interface Side {
  id: UnitId
  playerIds: PlayerId[]
}

/** A head-to-head contest inside a foursome. Two 1v1s, or one 2v2. */
export interface Pairing {
  id: string
  matchId: MatchId
  groupId: GroupId
  index: number
  sides: [Side, Side]
  pinned: boolean
}

// ---------------------------------------------------------------------------
// Scores
// ---------------------------------------------------------------------------

/**
 * Guys pick up constantly in match play, and opponents concede putts. Modeling
 * a score as a bare number cannot express either, and treating a pick-up as a
 * blank silently drops the hole out of the match.
 */
export type HoleScoreValue =
  | { state: 'holed'; gross: number }
  /**
   * Picked up. Loses the hole in match play and scores nothing in quota — a
   * double bogey, whatever `mostLikely` says. Stroke play is the one format
   * that posts the number, because it needs one for every hole.
   */
  | { state: 'pickedUp'; mostLikely: number | null }
  /** Conceded by the opponent — the conceder loses the hole. */
  | { state: 'conceded' }

export interface HoleScore {
  roundId: RoundId
  playerId: PlayerId
  holeNo: number
  value: HoleScoreValue
  enteredBy: PlayerId
  updatedAt: number
  deviceId: string
}

// ---------------------------------------------------------------------------
// Tiebreaks
// ---------------------------------------------------------------------------

export type TiebreakStep =
  | { kind: 'countOf'; what: 'eagles' | 'birdies' | 'pars' | 'bogeys' }
  | { kind: 'placeInRound'; roundSeq: number }
  | { kind: 'lowestNetTotal' }
  | { kind: 'coinFlip' }

export interface TiebreakChain {
  steps: TiebreakStep[]
}

/**
 * A resolved tie. Coin flips MUST be persisted — otherwise the leaderboard
 * reshuffles itself every time anything recomputes.
 */
export interface TiebreakResolution {
  /** 'match:<id>' | 'standings' | 'seeding' */
  scope: string
  /** Sorted player ids — this is the identity of the tie. */
  contenders: PlayerId[]
  resolvedBy: TiebreakStep['kind']
  detail: string
  order: PlayerId[]
}

// ---------------------------------------------------------------------------
// Tournament
// ---------------------------------------------------------------------------

export interface TournamentSettings {
  buyIn: number
  venmoHandle: string
  /** Whether the eagles/birdies/pars/bogeys counters are gross or net. */
  tiebreakCounterBasis: 'gross' | 'net'
  standingsTiebreak: TiebreakChain
  seedingTiebreak: TiebreakChain
  /** Fraction of course handicap used in head-to-head formats. */
  handicapAllowance: number
  rngSeed: string
  reshuffleCounter: number
  /**
   * Who may add a round that COUNTS. Anybody may add one for fun.
   *
   * Adding a round on a tee at 4:05 cannot go through the Setup PIN — the man
   * holding the phone is whoever is standing there — so creation is open. What
   * is not open is money: a counting round puts points into the seeding and a
   * ladder into a purse that balances to the dollar, and that is a decision for
   * the few who run the week.
   *
   * Absent falls back to the men already flagged `isAdmin` on the roster, so a
   * document written before this existed still behaves sensibly.
   */
  canCountRounds?: PlayerId[]
  /**
   * The man who makes the book.
   *
   * A job, not an office. It used to be whoever held the Setup PIN, which meant
   * the only way to let somebody post a line was to hand him the draw, the
   * handicaps, the payouts and the reset button as well. Naming him here gives
   * him the odds screen and nothing else.
   *
   * Absent falls back to the admins, which is how it behaved before.
   */
  bookie?: PlayerId
  /**
   * When the week was declared started, if it has been.
   *
   * Records a fact rather than enforcing one — nothing is locked by it. It
   * exists because "have we started?" is otherwise only answerable by looking
   * for scores, which is wrong on the first tee of the first round.
   */
  startedAt?: string
  /**
   * When the championship's foursomes were last written from the standings.
   * Its presence is what lets the draw print NAMES against the flights —
   * before the fill, the flighted groups hold the optimizer's placeholders,
   * and printing those was announcing Wednesday four days early.
   */
  flightsFilledAt?: string
  /**
   * Which of the four pitched design directions the app wears.
   *
   * Tournament-wide rather than per phone: everyone is looking at the same
   * leaderboard all week, and a screen someone holds up across a cart should
   * look like the one being held up next to it. Absent means Broadcast, so an
   * existing tournament never changes appearance on its own.
   */
  theme?: 'broadcast' | 'broadcast-soft' | 'clubhouse' | 'fescue' | 'southcoast'
  /*
   * Everything about the trip that is not golf: the flight, the food, the ride
   * home. One free-text block, one fact per line.
   *
   * Deliberately unstructured. These details arrive in the group thread a few
   * at a time over months — a tail number here, a dinner reservation there —
   * and any schema invented today would fail to hold next week's message. A
   * block of lines takes whatever turns up and needs no migration to do it.
   */
  travel?: string
  /**
   * The day's logistics, keyed by round date (YYYY-MM-DD): dinner times,
   * massages, the optional Preserve loop, the Thursday departure clock.
   *
   * A date-keyed sibling of `travel`, with the same rule — free text, one fact
   * per line, no schema. "AM", "OPTIONAL" and "8:00–8:30 PM" do not fit a time
   * field, and a structure here would make next year's "tail number" a
   * migration instead of a line. Rendered inside each day's section of the
   * week view, where "when is dinner TONIGHT" is actually asked; `travel`
   * stays trip-wide and collapses once play starts.
   */
  dayNotes?: Record<string, string>
  /**
   * Massage slots, keyed by day (YYYY-MM-DD): a time and the man booked in.
   *
   * Structured where `dayNotes` stays free text, because a slot is two facts
   * that must not come apart — 7:20 means nothing without whose 7:20 it is.
   * `playerId` null is an OPEN slot, which is worth showing: it is the answer
   * to "can I still get one" without asking the room. Edited in Setup beside
   * the day notes; rendered inside the day on The week, where the tee times
   * live.
   */
  /**
   * The massage sheet, by day. Two tables run at once from Tuesday on, so a
   * slot is a time AND a table; `table` absent means the single chair the
   * sheet had before there were two.
   */
  massages?: Record<string, { time: string; table?: number; playerId: PlayerId | null }[]>
}

export interface Tournament {
  id: string
  name: string
  year: number
  venue: string
  startDate: string
  endDate: string
  settings: TournamentSettings
  players: Player[]
  courses: Course[]
  rounds: Round[]
  matches: Match[]
  groups: Group[]
  pairings: Pairing[]
  handicaps: RoundHandicap[]
  scores: HoleScore[]
  tiebreaks: TiebreakResolution[]
  /** Monotonic sync cursor. */
  version: number
  /**
   * Bumped when an admin clears the scores.
   *
   * The sync cursor cannot express "everything you already hold is gone" — it
   * only ever carries rows at a version ABOVE the one you last saw, so a device
   * that is not looking would keep showing a week of deleted scores forever.
   * A change in this number tells every client to drop its copy and refetch.
   */
  scoresEpoch?: number
}

// ---------------------------------------------------------------------------
// Engine output
// ---------------------------------------------------------------------------

export interface TiebreakCounters {
  eagles: number
  birdies: number
  pars: number
  bogeys: number
  worse: number
}

export const EMPTY_COUNTERS: TiebreakCounters = {
  eagles: 0,
  birdies: 0,
  pars: 0,
  bogeys: 0,
  worse: 0,
}

/**
 * The uniform display projection.
 *
 * Scoring shapes are irreducibly different — a match-play margin, a quota
 * surplus and a net total are not the same number, and pretending otherwise is
 * what makes "uniform" format modules sprout special cases. What IS uniform is
 * how a row renders and how rows sort. So every format produces this, the
 * leaderboard renders it with zero format knowledge, and only detail views
 * reach for the typed union below.
 */
export interface StatusChip {
  /** "3 UP" | "+4" | "11 - 7" | "Won 5 & 3" | "-2" */
  primary: string
  /** "thru 7" | "F" | "28 / 24" | "net 68" */
  secondary: string
  tone: 'win' | 'lose' | 'even' | 'neutral'
}

export type FormatDetail =
  | {
      kind: 'matchPlay'
      up: number
      remaining: number
      dormie: boolean
      closedOut: { up: number; toPlay: number } | null
      won: number
      lost: number
      halved: number
    }
  | {
      kind: 'pointsPerHole'
      points: number
      opponentPoints: number
      breakdown: { low: number; high: number }
    }
  | { kind: 'quota'; target: number; earned: number; delta: number; perHole: number[] }
  | {
      kind: 'strokes'
      grossTotal: number
      netTotal: number
      toParNet: number
      cappedHoles: number[]
    }

export interface HoleTab {
  holeNo: number
  par: number
  pops: Record<PlayerId, number>
  gross: Record<PlayerId, number | null>
  net: Record<PlayerId, number | null>
  /** +1 won, 0 halved, -1 lost, null pending. Drives the scorecard grid. */
  outcome: number | null
}

/** One competitive unit — a player, or a two-man side — after tabulation. */
export interface UnitTab {
  unitId: UnitId
  playerIds: PlayerId[]
  groupId: GroupId
  perHole: HoleTab[]
  thru: number
  complete: boolean
  /** Always higher-is-better, so ranking never branches on format. */
  rankValue: number
  detail: FormatDetail
  chip: StatusChip
  counters: Record<PlayerId, TiebreakCounters>
}

export interface Award {
  playerId: PlayerId
  matchId: MatchId
  place: number | null
  points: number
  cash: number
  /** "Won 3 & 2" | "Halved" | "2nd" | "Flight 1 Winner" */
  label: string
}

export interface Settlement {
  matchId: MatchId
  awards: Award[]
  tiebreaks: TiebreakResolution[]
}

export interface EngineWarning {
  code:
    | 'UNVERIFIED_COURSE'
    | 'INCOMPLETE_STROKE_TOTAL'
    | 'MISSING_HANDICAP'
    | 'PAIRING_INFEASIBLE'
  message: string
}

/** Everything a format module needs, and nothing it does not. */
export interface RoundContext {
  tournament: Tournament
  round: Round
  course: Course
  /** Only the holes this round plays, in play order. */
  holes: Hole[]
  groups: Group[]
  pairings: Pairing[]
  handicaps: Record<PlayerId, RoundHandicap>
  /** Signed strokes received, parallel to `holes`. */
  pops: Record<PlayerId, number[]>
  /** Parallel to `holes`; undefined entries mean nothing entered yet. */
  scores: Record<PlayerId, (HoleScoreValue | undefined)[]>
}

/** A unit before tabulation. */
export interface Unit {
  id: UnitId
  playerIds: PlayerId[]
  groupId: GroupId
  /** Units this one is directly measured against. Empty for field-ranked quota. */
  opposingUnitIds: UnitId[]
}
